// Assistant d'Aurélia (autopilot-api) : conversations, message de l'utilisateur, action confirmée sur une carte.
import { AxiosInstanceCreator } from '../../../api/AxiosInstance';
import { autopilotApiUrl } from './autopilotRunApi';

export interface PersonItem { prospect_id: string; name: string; title: string; company: string; state: string; subject?: string; note?: string;
    /** déjà traité dans l'assistant (« corrigé et enregistré le 30/10 ») */
    mark?: string }
export interface Classification { categorie: string; sous_categorie: string; theme: string }
export interface Taxonomy { categories: string[]; subcategories: string[]; themes: string[] }
/** Taxonomie retenue pour une correction, comparée à celle de l'utilisateur, et raisonnement appliqué */
export interface MailTaxonomy extends Classification { connue?: boolean; reclassee?: boolean; themes_nouveaux?: string[];
    avant?: Classification; raisonnement?: { source: string; titre?: string; niveau?: string } }
/** Raisonnement jugé par le test (reverse prompting) */
export interface ReasoningVerdict { id?: string; origine?: string; titre?: string; texte?: string; verdict: 'correct' | 'partiel' | 'incorrect';
    raison: string; mail_le_suit?: boolean; prospect_id?: string }

export type AssistantCard =
    | { type: 'people'; items: PersonItem[] }
    | { type: 'mail'; prospect_id: string | null; title: string; body: string; kind: 'agent' | 'correction' | 'colle';
        note?: string; note_title?: string; status?: string; reasoning?: string; mark?: string; taxonomie?: MailTaxonomy }
    | { type: 'reasoning_test'; prospect_id?: string; memoire?: boolean; deduit?: string; du_mail?: string; meilleur?: string;
        confiance?: number; verdicts: ReasoningVerdict[] }
    | { type: 'reasoning'; prospect_id: string | null; pv: string; title: string; text: string; origin: string;
        classification: Classification; taxonomy: Taxonomy; save_first?: boolean }
    | { type: 'fiche'; prospect_id: string; name: string; title: string; company: string; email: string; status: string;
        profile_url: string; subject: string; reasoning: string; contacts: Record<string, unknown> }
    | { type: 'confirm'; action: AssistantAction; label: string; danger: boolean; payload: Record<string, unknown>; summary: string }
    | { type: 'followup'; prospect_id: string; subject: string; body: string; name?: string }
    | { type: 'campaigns'; items: { id: string; persona: string; state: string; ready: number; sent: number; reason: string; created_at?: string }[] }
    | { type: 'choices'; items: string[] };

export type AssistantAction = 'api_call' | 'send_test' | 'send_mail' | 'save_correction' | 'apply_reasoning' | 'save_reasoning' | 'send_followup'
    | 'launch_url' | 'launch_campaign' | 'select_person' | 'compose' | 'retry_correction' | 'save_category_rule';

/** Une étape de ce qu'Aurélia a fait : compris, outil lancé (paramètres, résultat), question, réponse */
export interface TraceStep {
    type: 'comprendre' | 'penser' | 'outil' | 'action' | 'analyser' | 'question' | 'repondre' | string;
    label: string; comprehension?: string; domaines?: string[]; certitude?: number; choix?: string[];
    outil?: string; action?: string; arguments?: Record<string, unknown>; resultat?: string; duree_ms?: number;
}

export interface AssistantMessage { id: string; role: 'user' | 'assistant'; text: string; cards: AssistantCard[]; at: string;
    trace?: TraceStep[] }
export interface PastedMail { title: string; body: string }
export interface ConversationSummary { id: string; title: string; updated_at: string; kind?: string; unread?: boolean }
export interface Conversation { id: string; conversation_id: string; title: string; messages: AssistantMessage[];
    focus: Record<string, unknown>; pending: boolean; steps?: { label: string; at: string; detail?: TraceStep }[];
    /** ce qu'Aurélia fait en ce moment, entre deux étapes (réflexion, worker appelé) */
    activity?: { label: string; at: string; phase?: string } | null;
    /** correction en masse en cours : les mails corrigés arrivent un par un */
    working?: boolean;
    /** message envoyé pendant qu'elle travaillait : gardé, lu par le tour en cours */
    queued?: boolean }

const http = () => AxiosInstanceCreator(autopilotApiUrl(), 'autopilot');

/** Le serveur n'a jamais répondu (connexion coupée, délai dépassé) : rien n'est parti, le message est à renvoyer */
export const NO_CONNECTION = "La connexion au serveur d'Aurélia a été coupée : votre message n'est pas parti. Renvoyez-le dans un instant.";

export const detail = (e: any, fallback: string): string => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string' && d.trim()) return d;
    return e && !e.response && (e.request || e.code === 'ECONNABORTED' || e.code === 'ERR_NETWORK') ? NO_CONNECTION : fallback;
};

export interface AssistantApi {
    list(): Promise<ConversationSummary[]>;
    get(id: string): Promise<Conversation>;
    remove(id: string): Promise<void>;
    chat(text: string, conversationId?: string | null, pasted?: PastedMail | null, persona?: string): Promise<Conversation>;
    act(conversationId: string, action: AssistantAction, payload: Record<string, unknown>, persona?: string): Promise<Conversation>;
    /** Arrête la réponse en cours (facultatif dans les tests) */
    stop?(conversationId: string): Promise<Conversation>;
}

export const assistantApi: AssistantApi = {
    async list() {
        return ((await http().get('assistant/conversations')).data?.items || []) as ConversationSummary[];
    },
    async get(id) {
        return (await http().get(`assistant/conversations/${id}`)).data;
    },
    async remove(id) {
        await http().delete(`assistant/conversations/${id}`);
    },
    async chat(text, conversationId, pasted, persona) {
        try {
            return (await http().post('assistant/chat', {
                text, conversation_id: conversationId || null, ...(pasted ? { pasted_mail: pasted } : {}),
                ...(persona ? { persona } : {}),
            })).data as Conversation;
        } catch (e) {
            throw new Error(detail(e, 'Aurélia ne répond pas pour le moment.'));
        }
    },
    async stop(conversationId) {
        try {
            return (await http().post(`assistant/conversations/${conversationId}/stop`)).data as Conversation;
        } catch (e) {
            throw new Error(detail(e, "Impossible d'arrêter pour le moment."));
        }
    },
    async act(conversationId, action, payload, persona) {
        try {
            return (await http().post(`assistant/conversations/${conversationId}/act`, { action, payload, persona })).data as Conversation;
        } catch (e) {
            throw new Error(detail(e, "L'action n'a pas pu être faite."));
        }
    },
};
